import { Suspense, lazy } from 'react'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'

import Button from '../../../../app/components/Button'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import { showAuthModal } from '../../../../auth/store/authSlice'
import useAppSelector from '../../../../../store/hooks/useAppSelector'
import {
  authCustomerIsLoggedInSelector,
  authInitializedSelector,
} from '../../../../auth/store/authSelectors'
import ClientAuthContainer from '../../../../auth/components/ClientAuthContainer'
import useRouteMatch from '../../../../app/hooks/useRouteMatch'
import {
  AppRoutes,
  AppTestIds,
  CartHiddenRoutes,
} from '../../../../app/constants'
import FillYourInfoModal from '../../../../auth/components/FillYourInfoModal'
import { AuthModalType } from '../../../../auth/types'
import useDeviceSize from '../../../../app/hooks/useDeviceSize'
import AuthSignInURLFilter from '../../../../auth/components/AuthSignInURLFilter'
import { preOrderUserSelector } from '../../../../preOrder/store/preOrderSelectors'
import AuthSignUpURLFilter from '../../../../auth/components/AuthSignUpURLFilter'
import isAppWhiteLabeled from '../../../../app/utils/isAppWhiteLabeled'
import useIsGiftCardPreviewRoute from '../../../../giftCards/hooks/useIsGiftCardPreviewRoute'
import ProfileMenu from '../profileMenu/ProfileMenu'
import InitialCheckoutValidation from '../../../../checkout/components/InitialCheckoutValidation'

import NavLanguageSwitcher from './NavLanguageSwitcher'
import styles from './NavActions.module.scss'

const AuthModal = lazy(() => import('../../../../auth/components/authModal'))
const ProfileSelectionModalV2 = lazy(
  () =>
    import('../../../../auth/components/profileSelection/ProfileSelectionModalV2'),
)
const WelcomeModal = lazy(
  () => import('../../../../auth/components/welcome/WelcomeModal'),
)
const CompanyMemberVerificationPendingModal = lazy(
  () =>
    import('../../../../company/components/CompanyMemberVerificationPendingModal'),
)
const CompanySignUpModal = lazy(
  () => import('../../../../auth/components/companySignUp/CompanySignUpModal'),
)
const ReviewAccountDetailsModal = lazy(
  () =>
    import('../../../../auth/components/reviewAccount/ReviewAccountDetailsModal'),
)

export default function NavActions() {
  const { t } = useTranslation('common')

  const dispatch = useAppDispatch()

  const isWhiteLabeled = isAppWhiteLabeled()

  const isLoggedIn = useAppSelector(authCustomerIsLoggedInSelector)
  const isAuthInitialized = useAppSelector(authInitializedSelector)
  const shouldHideCartButton = useRouteMatch(CartHiddenRoutes)
  const { isMobile } = useDeviceSize()
  const preOrderUser = useAppSelector(preOrderUserSelector)

  const isAuthRoute = useRouteMatch([AppRoutes.Auth])
  const isGiftCardPreviewRoute = useIsGiftCardPreviewRoute()

  const isNavActionsEligibleRoute = !isAuthRoute && !isGiftCardPreviewRoute

  const isPreOrderStartRoute = useRouteMatch([
    AppRoutes.PreOrderLink,
    AppRoutes.GroupOrder,
  ])

  const isShowProfile = !!isLoggedIn || !!preOrderUser
  // Prevent showing sign-in button when isLoggedIn = null
  const isShowSignIn =
    isLoggedIn === false && !preOrderUser && !isPreOrderStartRoute

  function handleSignInClick() {
    dispatch(showAuthModal(AuthModalType.SignIn))
  }

  return (
    <>
      {isNavActionsEligibleRoute && (
        <div className={styles.actionButtonContainer}>
          {/* The cart lives in its rail and drawer, not the top bar */}
          {!shouldHideCartButton && (
            <ClientAuthContainer fallbackSpinning={false}>
              <InitialCheckoutValidation />
            </ClientAuthContainer>
          )}
          {!isMobile && (
            <>
              {isShowProfile && <ProfileMenu />}
              {isShowSignIn && (
                <Button
                  size="large"
                  variant="tertiary"
                  onClick={handleSignInClick}
                  disabled={!isAuthInitialized}
                  className={clsx(styles.signInButton, {
                    [styles.whiteLabeled]: isWhiteLabeled,
                  })}
                  data-testid={AppTestIds.NavBar.Button.SignIn}
                >
                  {t('components.navBar.signIn')}
                </Button>
              )}
            </>
          )}
          <ClientAuthContainer fallbackSpinning={false}>
            <Suspense>
              <AuthModal />
              <FillYourInfoModal />
              <ProfileSelectionModalV2 />
              <CompanyMemberVerificationPendingModal />
              <AuthSignInURLFilter />
              <AuthSignUpURLFilter />
              {isWhiteLabeled && (
                <>
                  <CompanySignUpModal />
                  <ReviewAccountDetailsModal />
                  <WelcomeModal />
                </>
              )}
            </Suspense>
          </ClientAuthContainer>
        </div>
      )}
      <NavLanguageSwitcher />
    </>
  )
}
